import { AxiosError, AxiosInstance, AxiosResponse } from 'axios'

import { ssoLogin } from '@percent/workplace-giving/api/auth/ssoLogin/ssoLogin'
import {
  AuthAction,
  getLoginType
} from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import { logRateLimitHit } from '@percent/workplace-giving/utils/logger'

export const RATE_LIMIT_HIT = 'rate-limit-hit'

const WAIT_FOR_LOGOUT_MS = 1000

const waitForLogout = (ms: number) =>
  new Promise<undefined>(resolve => {
    setTimeout(resolve, ms)
  })

export const getSsoLoginUrl = async (client: AxiosInstance, email: string, redirectUrl?: string) => {
  const res = await ssoLogin(client, { email, redirectUrl })

  return res.result === 'error' ? null : res.data.loginUrl
}

const interceptUnauthorised = async (
  dispatch: (_: AuthAction) => void,
  client: AxiosInstance,
  email: string,
  waitMs: number
) => {
  if (!email) {
    dispatch({
      type: 'LOGOUT'
    })

    return waitForLogout(waitMs)
  }
  try {
    const loginType = getLoginType()

    if (loginType === 'sso') {
      const loginUrl = await getSsoLoginUrl(client, email)

      if (!loginUrl) {
        dispatch({
          type: 'LOGOUT'
        })

        return waitForLogout(waitMs)
      }
      dispatch({
        type: 'LOADING'
      })
      window.location.href = loginUrl

      return waitForLogout(waitMs)
    }
    dispatch({
      type: 'LOGOUT'
    })

    return waitForLogout(waitMs)
  } catch (e) {
    dispatch({
      type: 'LOGOUT'
    })

    return waitForLogout(waitMs)
  }
}

export const parseRetryAfter = (retryAfter: string | undefined) => {
  return retryAfter ? parseInt(retryAfter, 10) * 1000 : undefined
}

export const responseSuccessInterceptor =
  (
    dispatch: (_: AuthAction) => void,
    client: AxiosInstance,
    email: string,
    setRateLimitHit: (_: { retryAfter?: number | undefined }) => void
  ) =>
  async (response: AxiosResponse): Promise<AxiosResponse> => {
    if (response.status === 401) {
      await interceptUnauthorised(dispatch, client, email, 1000)

      return response
    }

    if (response.status === 429) {
      logRateLimitHit()
      setRateLimitHit({
        retryAfter: parseRetryAfter(response.headers['Retry-After'])
      })

      return Promise.reject(new Error(RATE_LIMIT_HIT))
    }

    return response
  }

export const responseFailureInterceptor =
  (
    dispatch: (_: AuthAction) => void,
    client: AxiosInstance,
    email: string,
    setRateLimitHit: (_: { retryAfter?: number | undefined }) => void,
    waitForLogoutMs?: number
  ) =>
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      return interceptUnauthorised(dispatch, client, email, waitForLogoutMs ?? WAIT_FOR_LOGOUT_MS)
    }

    if (error.response?.status === 429) {
      logRateLimitHit()
      setRateLimitHit({
        retryAfter: parseRetryAfter(error.response.headers['Retry-After'])
      })

      return Promise.reject(new Error(RATE_LIMIT_HIT))
    }

    return Promise.reject(error)
  }
